import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { ChatPanel } from './components/chat/ChatPanel';
import { ActivityPanelContainer } from './components/activity-panel/ActivityPanelContainer';
import colorsStore from 'shared/store/colorsStore';
import uiStore from 'shared/store/uiStore';
import useAppStatus from 'shared/store/appStatusStore';
import { useTabletAndBelowMediaQuery } from 'shared/store/responsiveStore';

import mainStyles from 'style/main.module.scss';
import styles from './assistantPage.module.scss';

const AssistantPageContent: React.FC = () => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const menuVisible = uiStore((state) => state.menuVisible);
  const isMenuClosing = uiStore((state) => state.isMenuClosing);
  const appStatus = useAppStatus((state) => state.appStatus);
  const isTabletOrBelow = useTabletAndBelowMediaQuery();

  const [isActivityPanelClosing, setIsActivityPanelClosing] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if activity panel should be expanded
  const isActivityPanelOpen = appStatus !== 'idle';

  // For desktop: always show the activity panel container
  // For mobile: show it when menu is visible OR when activity panel is expanded
  const shouldShowActivityPanel = !isTabletOrBelow || menuVisible;

  // Handle closing animation for mobile activity panel
  useEffect(() => {
    if (isTabletOrBelow && isActivityPanelOpen && !menuVisible) {
      // Activity panel is open but menu is closing - start closing animation
      setIsActivityPanelClosing(true);

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        setIsActivityPanelClosing(false);
      }, 300); // Match animation duration
    } else {
      setIsActivityPanelClosing(false);
    }
  }, [isTabletOrBelow, isActivityPanelOpen, menuVisible]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className={mainStyles.mainContainer}
      style={{
        backgroundColor: colorsConfig.white,
      }}
    >
      <div className="relative flex h-full flex-row flex-nowrap">
        {shouldShowActivityPanel && (
          <div
            className={classNames(styles.activityPanelContainer, {
              [styles.activityPanelContainerHiding]: isTabletOrBelow && (isMenuClosing || isActivityPanelClosing),
            })}
          >
            <div className={styles.activityPanelInnerContainer}>
              <ActivityPanelContainer />
            </div>
          </div>
        )}

        <div
          className="h-full max-h-full w-full max-w-full flex-grow"
          style={{
            backgroundColor: colorsConfig.white,
          }}
        >
          <ChatPanel />
        </div>
      </div>
    </div>
  );
};

export const AssistantPage: React.FC = () => {
  return <AssistantPageContent />;
};
